/**
 * Timezone Context for BetterMD
 * Manages timezone selection and practitioner timezone mapping
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TimezoneValue, DEFAULT_TIMEZONE, TIMEZONES } from '../utils/timezone';
import { useAuth } from './AuthContext';

// Practitioner timezone mapping interface
export interface PractitionerTimezone {
  id: string;
  name: string;
  timezone: TimezoneValue;
}

// Context interface
interface TimezoneContextType {
  // Current selected timezone for display
  selectedTimezone: TimezoneValue;
  setSelectedTimezone: (timezone: TimezoneValue) => void;
  
  // Practitioner timezone mappings
  practitionerTimezones: PractitionerTimezone[];
  setPractitionerTimezones: (practitioners: PractitionerTimezone[]) => void;
  
  // Helper functions
  getPractitionerTimezone: (practitionerId: string) => TimezoneValue;
  setPractitionerTimezone: (practitionerId: string, timezone: TimezoneValue) => void;
  
  // Loading state
  isLoading: boolean;
}

// Create context
const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

// Provider props
interface TimezoneProviderProps {
  children: ReactNode;
  initialTimezone?: TimezoneValue;
}

// Provider component
export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({
  children,
  initialTimezone = DEFAULT_TIMEZONE
}) => {
  const { user, token } = useAuth();
  const [selectedTimezone, setSelectedTimezone] = useState<TimezoneValue>(initialTimezone);
  const [practitionerTimezones, setPractitionerTimezones] = useState<PractitionerTimezone[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [timezoneLoaded, setTimezoneLoaded] = useState<boolean>(false);

  // Load practitioner timezones on initial mount
  useEffect(() => {
    const loadPractitionerTimezones = async () => {
      try {
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        
        const practitionerResponse = await fetch('/api/practitioner-timezones', {
          method: 'GET',
          headers
        });
        
        if (practitionerResponse.ok) {
          const data = await practitionerResponse.json();
          if (data.practitioner_timezones) {
            setPractitionerTimezones(data.practitioner_timezones);
            console.log(`Loaded ${data.practitioner_timezones.length} practitioner timezones from API`);
          }
        } else {
          console.log('Practitioner timezone API not available, using defaults');
          useDefaultPractitionerTimezones();
        }
      } catch (error) {
        console.error('Error loading practitioner timezones:', error);
        useDefaultPractitionerTimezones();
      }
    };

    loadPractitionerTimezones();
  }, []); // Only load practitioner timezones once
  
  // Load user timezone preference when user changes
  useEffect(() => {
    const loadUserTimezone = async () => {
      try {
        setIsLoading(true);
        setSelectedTimezone(DEFAULT_TIMEZONE); // Reset first
        
        if (user && token) {
          console.log(`🌏 Loading timezone preference for user: ${user.username} (ID: ${user.id})`);
          
          const headers: HeadersInit = { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          };
          
          const response = await fetch('/api/users/timezone-preference', {
            method: 'GET',
            headers
          });
          
          if (response.ok) {
            const userData = await response.json();
            console.log(`🌏 API Response for ${user.username}:`, userData);
            if (userData.timezone_preference && Object.values(TIMEZONES).includes(userData.timezone_preference as TimezoneValue)) {
              setSelectedTimezone(userData.timezone_preference as TimezoneValue);
              console.log(`🌏 Loaded timezone preference for ${user.username}: ${userData.timezone_preference}`);
            } else {
              console.log(`🌏 No valid timezone preference for ${user.username}, using default: ${DEFAULT_TIMEZONE}`);
            }
          } else {
            const errorText = await response.text();
            console.log(`🌏 API error for ${user.username}: ${response.status} - ${errorText}`);
          }
        } else {
          console.log('🌏 No user or token, using default timezone');
        }
      } catch (error) {
        console.error('Error loading user timezone preference:', error);
      } finally {
        setIsLoading(false);
        setTimezoneLoaded(true);
      }
    };

    loadUserTimezone();
  }, [user, token]); // Re-run when user or token changes


  // Default practitioner timezone mappings (until API is implemented)
  const useDefaultPractitionerTimezones = () => {
    const defaultMappings: PractitionerTimezone[] = [
      // Adelaide practitioners
      { id: '1630848246958006062', name: 'Better MD', timezone: TIMEZONES.ADELAIDE },
      { id: '1090893398618939363', name: 'Practitioner Kayla', timezone: TIMEZONES.ADELAIDE },
      { id: '1092417506427018338', name: 'Hydralyfe Australia', timezone: TIMEZONES.ADELAIDE },
      { id: '1137575448910241404', name: 'Integrated Naturopath Specialist', timezone: TIMEZONES.ADELAIDE },
      { id: '1188753988485390178', name: 'Nutritionist And Naturopath Tanya Burton', timezone: TIMEZONES.ADELAIDE },
      { id: '1351865736397594505', name: 'Nurse Selina', timezone: TIMEZONES.ADELAIDE },
      { id: '1377226939525242045', name: 'Practitioner Amy', timezone: TIMEZONES.ADELAIDE },
      { id: '1501190230118506026', name: 'Christina Halvorsen', timezone: TIMEZONES.ADELAIDE },
      { id: '1509311477159304832', name: 'Nurse Lauren', timezone: TIMEZONES.ADELAIDE },
      { id: '1553969390582504448', name: 'Brad Van elsen', timezone: TIMEZONES.ADELAIDE },
      { id: '1629247871771483926', name: 'Drip Hydration Nurse', timezone: TIMEZONES.ADELAIDE },
      { id: '1632845171248865081', name: 'Nurse Euphoria', timezone: TIMEZONES.ADELAIDE },
      
      // Sydney practitioners
      { id: '1559731141546613820', name: 'Nurse Melissa', timezone: TIMEZONES.SYDNEY },
      { id: '1573835076569930957', name: 'Doctor Leah', timezone: TIMEZONES.SYDNEY },
      { id: '1629256997201256215', name: 'Dr Dearra', timezone: TIMEZONES.SYDNEY },
      
      // Brisbane practitioners
      { id: '1424193881037086423', name: 'Practitioner Stephen', timezone: TIMEZONES.BRISBANE }
    ];
    
    setPractitionerTimezones(defaultMappings);
  };

  // Get timezone for a specific practitioner
  const getPractitionerTimezone = (practitionerId: string): TimezoneValue => {
    const practitioner = practitionerTimezones.find(p => p.id === practitionerId);
    return practitioner?.timezone || DEFAULT_TIMEZONE;
  };

  // Set timezone for a specific practitioner
  const setPractitionerTimezone = (practitionerId: string, timezone: TimezoneValue) => {
    setPractitionerTimezones(prev => {
      const existing = prev.find(p => p.id === practitionerId);
      if (existing) {
        return prev.map(p => 
          p.id === practitionerId 
            ? { ...p, timezone }
            : p
        );
      } else {
        // Add new practitioner mapping
        return [...prev, {
          id: practitionerId,
          name: `Practitioner ${practitionerId}`,
          timezone
        }];
      }
    });
  };

  // Save selected timezone to database via API
  const saveUserTimezonePreference = async (timezone: TimezoneValue) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers: HeadersInit = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      
      const response = await fetch('/api/users/timezone-preference', {
        method: 'PUT',
        headers,
        body: JSON.stringify({ timezone })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`🌏 Saved user timezone preference: ${timezone}`, result);
      } else {
        const errorText = await response.text();
        console.error(`Failed to save user timezone preference: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error saving user timezone preference:', error);
    }
  };


  // Save timezone preference when it changes (but not on initial load)
  useEffect(() => {
    if (timezoneLoaded && selectedTimezone) {
      console.log(`🌏 TimezoneContext: selectedTimezone changed to ${selectedTimezone}, saving to database`);
      saveUserTimezonePreference(selectedTimezone);
    }
  }, [selectedTimezone, timezoneLoaded]);

  const value: TimezoneContextType = {
    selectedTimezone,
    setSelectedTimezone,
    practitionerTimezones,
    setPractitionerTimezones,
    getPractitionerTimezone,
    setPractitionerTimezone,
    isLoading
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

// Hook to use timezone context
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

// Hook to get practitioner timezone specifically
export const usePractitionerTimezone = (practitionerId?: string): TimezoneValue => {
  const { getPractitionerTimezone } = useTimezone();
  return practitionerId ? getPractitionerTimezone(practitionerId) : DEFAULT_TIMEZONE;
};

// Hook for timezone-aware calendar operations
export const useCalendarTimezone = () => {
  const { selectedTimezone, setSelectedTimezone, practitionerTimezones } = useTimezone();
  
  return {
    displayTimezone: selectedTimezone,
    setDisplayTimezone: setSelectedTimezone,
    availableTimezones: Array.from(
      new Set(practitionerTimezones.map(p => p.timezone))
    ),
    practitionersByTimezone: practitionerTimezones.reduce((acc, p) => {
      if (!acc[p.timezone]) {
        acc[p.timezone] = [];
      }
      acc[p.timezone].push(p);
      return acc;
    }, {} as Record<TimezoneValue, PractitionerTimezone[]>)
  };
};